import type { EquipmentConfig, ExtraTagConfig, StreamConfig } from '../../engine/plant/types';

/**
 * Exothermic CSTR unit. Kinetics and design steady state: docs/PROCESS_MODELS.md §2.7
 * and §5.2. Reaction A → B, first order in A, 50 MJ/kmol exothermic, in a 4 m³ jacketed
 * agitated vessel at 20 minutes' residence.
 *
 * Species: A reactant, B product, S heavy solvent, L a dissolved light that flashes off
 * in the separator and gives the pressure loop something to do.
 */

export const extraTags: ExtraTagConfig[] = [
  { name: 'S-CW.Q', units: 'm3/h', rangeLo: 0, rangeHi: 60, decimals: 2, kind: 'state', description: 'Jacket cooling-water flow' },
  { name: 'S-101.Q', units: 'm3/h', rangeLo: 0, rangeHi: 20, decimals: 2, kind: 'state', description: 'Feed A flow' },
  { name: 'S-102.Q', units: 'm3/h', rangeLo: 0, rangeHi: 12, decimals: 2, kind: 'state', description: 'Feed B flow' },
  { name: 'S-203.Q', units: 'm3/h', rangeLo: 0, rangeHi: 30, decimals: 2, kind: 'state', description: 'Reactor outlet flow' },
  { name: 'S-302.Q', units: 'm3/h', rangeLo: 0, rangeHi: 30, decimals: 2, kind: 'state', description: 'Product flow' },
  { name: 'AG-201.RUN', units: '', rangeLo: 0, rangeHi: 1, decimals: 0, kind: 'discrete', trendable: false, description: 'Agitator run command', initial: 1 },
];

export const equipment: EquipmentConfig[] = [
  // ── Feed ────────────────────────────────────────────────────────────────
  {
    id: 'SRC-A',
    name: 'Feed A from storage',
    type: 'source',
    pressure: 8e5,
    temperature: 298.15,
    // 1.5 kmol/m³ of A in a heavy solvent, with a dissolved light.
    composition: { A: 1.5, B: 0, S: 6.0, L: 2.0 },
    profiles: [{ field: 'A', kind: 'constant', mean: 1.5, noiseSigma: 0.004, noisePhi: 0.999, lo: 0, hi: 4 }],
  },
  {
    id: 'SRC-B',
    name: 'Feed B — solvent',
    type: 'source',
    pressure: 8e5,
    temperature: 298.15,
    composition: { A: 0, B: 0, S: 7.5, L: 0.5 },
  },
  {
    id: 'FV-101',
    name: 'Feed A control valve',
    type: 'valve',
    cv: 12,
    characteristic: 'equalPercentage',
    tau: 3,
    deadband: 0.5,
    failMode: 'failClosed',
    commandTag: 'FV-101.CMD',
    initialPosition: 0.62,
    positionTransmitter: true,
  },
  {
    id: 'FV-102',
    name: 'Feed B control valve',
    type: 'valve',
    cv: 6,
    characteristic: 'equalPercentage',
    tau: 3,
    deadband: 0.5,
    failMode: 'failClosed',
    commandTag: 'FV-102.CMD',
    initialPosition: 0.62,
    positionTransmitter: true,
  },
  { id: 'HD-101', name: 'Feed header', type: 'header', pressure: 5.0e5, capacitance: 2e-8, temperature: 298.15 },
  {
    id: 'HX-103',
    name: 'Feed preheater',
    type: 'hx',
    // The hot side is the process; the utility is hot oil, so the duty runs the other way
    // and `HX-103.DUTY` reads negative. UA is high enough that the exchanger is
    // flow-limited: the oil valve, not the surface area, sets the outlet temperature.
    ua: 1200,
    volHot: 0.3,
    volCold: 0.2,
    rhoCpHot: 239e3,
    rhoCpCold: 1.9e6, // hot oil, 850 kg/m³ × 2.2 kJ/(kg·K)
    coldSupplyT: 340,
    coldFlowTag: 'HX-103.UTIL',
  },
  {
    id: 'FV-103',
    name: 'Preheater hot-oil valve',
    type: 'valve',
    cv: 8,
    characteristic: 'linear',
    tau: 4,
    failMode: 'failClosed',
    commandTag: 'FV-103.CMD',
    initialPosition: 0.4,
  },

  // ── Reactor ─────────────────────────────────────────────────────────────
  {
    id: 'R-201',
    name: 'Reactor',
    type: 'cstr',
    volume: 5.0,
    levelArea: 2.0,
    initialLevel: 2.0, // 4 m³ of liquid, 80 % level
    initialT: 350.0,
    pressure: 4.5e5,
    k0: 7.2e10 / 60, // 1/s  [SEM]
    eOverR: 8750, // K  [SEM]
    dHr: -50e6, // J/kmol  [SEM]
    rhoCp: 239e3, // J/(m³·K)  [SEM]
    ua0: 2830, // W/K, derived from the design duty
    uaAgitatorOffFactor: 0.35,
    uaTau: 120,
    jacketVolume: 0.6,
    jacketRhoCp: 4.18e6,
    jacketSupplyT: 298.15,
    jacketSupplyTag: 'SRC-CW.T',
    jacketFlowTag: 'S-CW.Q',
    agitatorTag: 'AG-201.RUN',
    reactant: 'A',
    product: 'B',
    initialComposition: { A: 0.0476, B: 0.952, S: 6.5, L: 1.5 },
    substeps: 10,
  },
  {
    id: 'FV-203',
    name: 'Reactor outlet valve',
    type: 'valve',
    cv: 20,
    characteristic: 'linear',
    tau: 3,
    failMode: 'failClosed',
    commandTag: 'FV-203.CMD',
    initialPosition: 0.5,
    positionTransmitter: true,
  },

  // ── Cooling water ───────────────────────────────────────────────────────
  {
    id: 'SRC-CW',
    name: 'Cooling-water supply',
    type: 'source',
    // Supply and return sit at the same pressure, so the pump provides all the driving
    // force. Trip it and the jacket flow really does go to zero — which is the whole
    // point of the scenario.
    pressure: 1.5e5,
    temperature: 298.15, // 25 °C
    composition: {},
    profiles: [
      // A slow daily swing; the summer scenario ramps the mean up on top of it.
      { field: 'temperature', kind: 'diurnal', mean: 298.15, amplitude: 1.2, periodS: 86400, phaseS: 32400, noiseSigma: 0.0004, noisePhi: 0.999, lo: 283, hi: 320 },
    ],
  },
  {
    id: 'CWP-201',
    name: 'Cooling-water pump',
    type: 'pump',
    h0: 60,
    kCurve: 80_000,
    qBep: 0.01,
    etaBep: 0.72,
    vfd: false,
    commandTag: 'CWP-201.CMD',
    startTime: 6,
    coastTime: 10,
    npshr: 2,
  },
  {
    id: 'FV-202',
    name: 'Jacket cooling-water valve',
    type: 'valve',
    cv: 30,
    characteristic: 'equalPercentage',
    tau: 3,
    deadband: 0.4,
    failMode: 'failOpen', // fail to full cooling — the safe state for an exotherm
    commandTag: 'FV-202.CMD',
    initialPosition: 0.5,
    positionTransmitter: true,
  },
  { id: 'SNK-CW', name: 'Cooling-water return', type: 'sink', pressure: 1.5e5 },

  // ── Emergency quench ────────────────────────────────────────────────────
  {
    id: 'SRC-Q',
    name: 'Quench header',
    type: 'source',
    pressure: 9e5,
    temperature: 288.15,
    composition: { A: 0, B: 0, S: 9.5, L: 0.2 },
  },
  {
    id: 'FV-901',
    name: 'Emergency quench valve',
    type: 'valve',
    cv: 60,
    characteristic: 'linear',
    tau: 1.5,
    strokeTime: 4,
    failMode: 'failClosed',
    commandTag: 'FV-901.CMD',
    initialPosition: 0,
    positionTransmitter: true,
  },
  {
    id: 'CALC-103',
    name: 'Preheater hot-oil flow',
    type: 'calc',
    out: { name: 'HX-103.UTIL', units: 'm3/h', rangeLo: 0, rangeHi: 4, decimals: 3, description: 'Preheater hot-oil flow' },
    ops: [
      { op: 'tag', tag: 'FV-103.POS' },
      { op: 'mul', value: 0.015 }, // 1.5 m³/h of hot oil fully open
    ],
  },
  {
    id: 'CALC-901',
    name: 'Quench flow',
    type: 'calc',
    out: { name: 'CALC-901.OUT', units: 'm3/h', rangeLo: 0, rangeHi: 40, decimals: 2, description: 'Emergency quench flow' },
    ops: [
      { op: 'tag', tag: 'FV-901.POS' },
      { op: 'mul', value: 0.36 }, // 36 m³/h fully open
    ],
  },

  // ── Separation and product ──────────────────────────────────────────────
  {
    id: 'V-301',
    name: 'Flash separator',
    type: 'separator',
    volume: 3.0,
    initialLevel: 0.5,
    temperature: 348,
    initialPressure: 2.0e5,
    pRef: 2.0e5,
    tRef: 350,
    kRef: { A: 3.0, B: 0.4, S: 0.08, L: 9.0 },
    kTempCoeff: { A: 2000, B: 2400, S: 2800, L: 1500 },
    ventValve: 'FV-301',
    liquidValve: 'FV-302',
  },
  {
    id: 'FV-301',
    name: 'Separator vent valve',
    type: 'valve',
    cv: 60,
    characteristic: 'equalPercentage',
    tau: 4,
    failMode: 'failOpen', // venting is the safe state on a pressure vessel
    commandTag: 'FV-301.CMD',
    initialPosition: 0.5,
    specificGravity: 0.005,
    positionTransmitter: true,
  },
  {
    id: 'FV-302',
    name: 'Separator level valve',
    type: 'valve',
    cv: 25,
    characteristic: 'linear',
    tau: 3,
    failMode: 'failClosed',
    commandTag: 'FV-302.CMD',
    initialPosition: 0.5,
    positionTransmitter: true,
  },
  {
    id: 'TK-401',
    name: 'Product tank',
    type: 'tank',
    shape: 'vertical',
    area: 6,
    height: 4,
    initialLevel: 1.6,
    open: true,
    temperature: 340,
    composition: { A: 0.02, B: 1.05, S: 7.2, L: 0.1 },
  },
  { id: 'SNK-PROD', name: 'Product export', type: 'sink', pressure: 1.05e5 },
];

/** Streams in topological order — `transfer` branches need their source fed first. */
export const streams: StreamConfig[] = [
  { id: 'S-101', from: 'SRC-A', to: 'HD-101', kind: 'pressure', valve: 'FV-101', k: 2e9, qMax: 0.02, flowOutTag: 'S-101.Q' },
  { id: 'S-102', from: 'SRC-B', to: 'HD-101', kind: 'pressure', valve: 'FV-102', k: 4e9, qMax: 0.02, flowOutTag: 'S-102.Q' },
  { id: 'S-103', from: 'HD-101', to: 'HX-103', kind: 'pressure', k: 2.7e9, qMax: 0.03 },
  { id: 'S-104', from: 'HX-103', to: 'R-201', kind: 'transfer' },
  { id: 'S-901', from: 'SRC-Q', to: 'R-201', kind: 'demand', flowTag: 'CALC-901.OUT' },
  { id: 'S-203', from: 'R-201', to: 'V-301', kind: 'pressure', valve: 'FV-203', k: 1e8, qMax: 0.06, flowOutTag: 'S-203.Q' },
  { id: 'S-302', from: 'V-301', to: 'TK-401', kind: 'pressure', valve: 'FV-302', k: 1e8, qMax: 0.05, checkValve: true, flowOutTag: 'S-302.Q' },
  { id: 'S-401', from: 'TK-401', to: 'SNK-PROD', kind: 'weir', weirLength: 1.5, weirCrest: 1.5 },
  // Cooling water is a separate circuit; only its measured flow reaches the reactor model.
  { id: 'S-CW', from: 'SRC-CW', to: 'SNK-CW', kind: 'pressure', pump: 'CWP-201', valve: 'FV-202', k: 2e9, qMax: 0.03, checkValve: true, flowOutTag: 'S-CW.Q' },
];
